<template>
  <b-container fluid>
    <b-row>
      <b-col cols="12">
        <PageHeader
          :page="header"
          left-side-class="col-12 col-lg-8"
          right-side-class="col-12 col-lg-4 d-flex align-items-center justify-content-end"
          has-return
        >
          <template v-slot:customActions>
            <div v-if="activeTab === tabs.ACTIONPLAN">
              <dropdown title="Acciones" :items="actionPlanActions" />
            </div>
            <div v-else>
              <ActionButton
                variant="success m-1"
                text="Generar Alternativas"
                i="fas fa-layer-plus"
                :responsive="false"
                @click="buildNexAlternatives()"
              />
            </div>
          </template>
        </PageHeader>
      </b-col>
    </b-row>

    <div class="mb-4">
      <q-tab :active-tab="activeTab">
        <q-tab-item
          title="Escuela"
          :tabindex="tabs.SCHOOL"
          @click="setCurrentTab(tabs.SCHOOL)"
        >
          <b-row>
            <b-col cols="12">
              <SearchFilter
                ref="SearchFilter"
                :result.sync="result"
                :pagination.sync="pagination"
                :filter.sync="filter"
                :advanced-filter.sync="advancedFilter"
              />
            </b-col>
            <b-col cols="12" class="pt-4">
              <SearchResult
                :result="result"
                :pagination.sync="pagination"
                @onPageChanged="onPageChanged"
              />
            </b-col>
          </b-row>
        </q-tab-item>
        <q-tab-item
          title="Plan de Acción"
          :tabindex="tabs.ACTIONPLAN"
          @click="setCurrentTab(tabs.ACTIONPLAN)"
        >
          <action-plan ref="actionPlanRef" />
        </q-tab-item>
      </q-tab>
    </div>
  </b-container>
</template>

<script>
import PageHeader from 'PageHeader';
import ActionButton from 'ActionButton';
import Dropdown from 'dropdown';
import alert from '@/mixin/alert.mixin';
import SearchFilter from './_components/SearchFilter';
import SearchResult from './_components/SearchResult';
import { initialPagination } from './_utils';
import { buildToNextYear } from '@/services/api/alternativeLocation';
import { QTab, QTabItem } from '@/components/common/q-tabs';
import store from '@/store/index';
import storeMixin from '@/mixin/storeGenerator';
import ActionPlan from './actionPlan/Main.vue';
import reportService from '@/services/report';
import { sendReport } from '@/services/operations/action-plan.js';

const TABS = {
  SCHOOL: 0,
  ACTIONPLAN: 1,
};

export default {
  name: 'LocationAlternativesSearch',
  mixins: [alert, storeMixin],
  components: {
    PageHeader,
    SearchFilter,
    SearchResult,
    ActionButton,
    QTab,
    QTabItem,
    ActionPlan,
    Dropdown,
  },
  data: () => ({
    header: {
      icon: 'fas fa-user-tie',
      title: 'Catálogo de Alternativa de Ubicación',
      routes: ['Inicio', 'PEI', 'Catálogo de Alternativa de Ubicación'],
    },
    result: [],
    pagination: initialPagination(),
    filter: {},
    advancedFilter: false,
  }),
  methods: {
    async dispose() {
      this.$refs.SearchFilter.initFilter();
    },
    onPageChanged() {
      this.$refs.SearchFilter.search(this.pagination);
    },
    async buildNexAlternatives() {
      try {
        await buildToNextYear();
        this.ShowToast('', 'Alternativas generadas exitosamente.', 'success');
      } catch {
        this.ShowToast('Error', 'Error al generar las alternativas', 'error');
      }
    },
    setCurrentTab(activeTab) {
      this.$router.replace({
        query: {
          ...this.$route.query,
          tab: activeTab,
        },
      });
    },
    handleAddPlan() {
      const actionPlanRef = this.$refs.actionPlanRef;
      if (actionPlanRef) {
        const academicYearId = actionPlanRef.filter?.model?.academicYear?.id;
        if (academicYearId) {
          this.$router.push({
            name: 'location-alternatives-action-plan',
            params: {
              academicYearId,
            },
          });
        }
      }
    },
    async downloadReport() {
      const actionPlanRef = this.$refs.actionPlanRef;
      const filterRef = actionPlanRef?.$refs?.filterActionPlan;

      // Activar validaciones y filtros avanzados
      actionPlanRef.shouldValidateCenter = true;
      filterRef.advanced = true;
      // Validar formulario
      await filterRef?.$refs?.formValidator?.validate();

      // Verifica filtros requeridos para el report
      const academicYearId = actionPlanRef?.filter?.model?.academicYear?.id;
      const centerId = filterRef?.value?.center?.id;
      const oreId = filterRef?.value?.ore?.id;

      if (!academicYearId || (!centerId && !oreId)) return;

      if (centerId && oreId) {
        this.ShowToast(
          '',
          'Debe seleccionar ORE o Centro, pero no ambos. Ajuste su selección para continuar con la impresión del reporte.',
          'error'
        );
        return;
      }

      await reportService.getActionPlanReport({
        academicYearId,
        centerId,
        oreId,
      });
    },
    async sentReport() {
      const actionPlanRef = this.$refs.actionPlanRef;
      const filterRef = actionPlanRef?.$refs?.filterActionPlan;

      // Activar validaciones y filtros avanzados
      actionPlanRef.shouldValidateCenter = true;
      filterRef.advanced = true;

      // Validar formulario
      await filterRef?.$refs?.formValidator?.validate();

      // Verifica filtros requeridos para el report
      const academicYearId = actionPlanRef?.filter?.model?.academicYear?.id;
      const centerId = filterRef?.value?.center?.id;

      if (!academicYearId || !centerId) return;
      try {
        await sendReport({
          academicYearId,
          centerId,
          staffAssignmentResourceConfirmed: '3',
        });
        this.ShowToast('', 'Reporte enviado exitosamente.', 'success');
      } catch (error) {
        this.ShowToast('', 'Error al enviar reporte.', 'error');
      }
    },
  },
  computed: {
    actionPlanActions() {
      const items = [
        {
          label: 'Descargar Reporte',
          icon: 'fas fa-file-download text-success',
          click: this.downloadReport,
        },
        {
          label: 'Enviar Reporte',
          icon: 'fas fa-paper-plane text-success',
          click: this.sentReport,
        },
      ];

      if (this.canViewCreateActionPlan) {
        items.unshift({
          label: 'Añadir Plan',
          icon: 'fas fa-file-plus text-success',
          click: this.handleAddPlan,
        });
      }

      return items;
    },
    activeTab() {
      const tab = Number(this.$route.query?.tab);
      return isNaN(tab) ? TABS.SCHOOL : tab;
    },
    canBuildNexAlternatives() {
      return store.getters.HasPermission(
        'academic.nextyearalternativelocation'
      );
    },
    tabs() {
      return { ...TABS };
    },
    canViewCreateActionPlan() {
      return (
        this.$store.getters.HasPermission(
          'academic.pei.alternativelocationcatalog.actionplan.create'
        ) && this.activeTab === TABS.ACTIONPLAN
      );
    },
  },
  destroyed() {
    this.persistData();
  },
  beforeRouteEnter(_, from, next) {
    const ROUTE_NAME_FROM = 'location-alternatives-manage';

    next((vm) => {
      if (from.name != ROUTE_NAME_FROM) {
        vm.shouldRecovery = false;
        vm.dispose();
      } else {
        vm.recoveryData();
        const filter = { ...vm.filter };
        const isAdvancedFilter = vm.advancedFilter;

        vm.$refs.SearchFilter.setFilter(filter);
        vm.$refs.SearchFilter.setAdvancedFilter(isAdvancedFilter);
      }
    });
  },
};
</script>
